import { useState, useEffect, useRef, useCallback } from 'react'
import { router } from '@inertiajs/react'
import useUndo from 'use-undo'
import styles from './Calendar.module.css'
import MiniCalendar from './MiniCalendar'
import WeeklyView from './WeeklyView'
import DayView from './DayView'
import LoadingOverlay from './LoadingOverlay'
import apiClient from '../utils/api'
import { getWeekDates, formatMonthYear, formatDateForBackend, moment, calculateDurationSeconds, getMondayOfWeek } from '../utils/dateUtils'
import { isFeatureAvailable } from '../utils/featureGate'

export default function Calendar({ apiToken, user = null, availableProjects = [], availableTasks = [], currentWorkspace = null }) {
    const today = moment().toDate()

    // Initialize currentDate from URL query param or fallback to today
    const [currentDate, setCurrentDate] = useState(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search)
            const weekParam = params.get('week')

            if (weekParam) {
                const monday = moment(weekParam, 'YYYY-MM-DD')
                if (monday.isValid()) {
                    console.log('Initializing currentDate from URL param:', weekParam)
                    return monday.toDate()
                }
            }
        }
        return today
    })

    const [selectedDate, setSelectedDate] = useState(today)
    const [worklogs, setWorklogs] = useState([])
    const [timeTotals, setTimeTotals] = useState({ daily_totals: {}, weekly_totals: {} })
    const [calendarEvents, setCalendarEvents] = useState([])
    const [weeklyExpanded, setWeeklyExpanded] = useState(true)
    const [monthlyExpanded, setMonthlyExpanded] = useState(true)
    const [viewMode, setViewMode] = useState('week') // 'day' or 'week'
    const [userPreferredViewMode, setUserPreferredViewMode] = useState('week') // User's preferred view when not on mobile
    const [isMobile, setIsMobile] = useState(false)
    const [showQuickNav, setShowQuickNav] = useState(false)
    const [selectedQuickNavLabel, setSelectedQuickNavLabel] = useState('Today')
    const quickNavRef = useRef(null)

    // Projects state
    const [projects, setProjects] = useState(availableProjects)
    const [tasks, setTasks] = useState(availableTasks)
    const [loadingProjects, setLoadingProjects] = useState(false)

    // Teammates state
    const [teammates, setTeammates] = useState([])
    const [selectedTeammates, setSelectedTeammates] = useState([])
    const [isAdmin, setIsAdmin] = useState(false)
    const [loadingTeammates, setLoadingTeammates] = useState(false)
    const [showTeammatesDropdown, setShowTeammatesDropdown] = useState(false)
    const [teammateSearchTerm, setTeammateSearchTerm] = useState('')
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const teammatesDropdownRef = useRef(null)

    // Workspace users state (for @mentions - available to all users)
    const [workspaceUsers, setWorkspaceUsers] = useState([])

    // Settings modal state
    const [showSettingsModal, setShowSettingsModal] = useState(false)
    const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState(false)

    // Project hover state for highlighting effect
    const [hoveredProjectId, setHoveredProjectId] = useState(null)

    // Weekend toggle state
    const [showWeekends, setShowWeekends] = useState(true)

    // Google Calendar visibility toggle state
    const [isGoogleCalendarVisible, setIsGoogleCalendarVisible] = useState(true)

    // Calendar view density state
    const [calendarViewDensity, setCalendarViewDensity] = useState('compact') // 'compact' or 'relaxed'

    // Loading state for worklog operations
    const [isLoading, setIsLoading] = useState(false)
    const [loadingMessage, setLoadingMessage] = useState('Updating...')

    // Success message for split worklogs
    const [successMessage, setSuccessMessage] = useState(null)

    // Undo functionality using use-undo library
    const [undoState, { set: setUndoState, undo, canUndo, reset: resetUndo }] = useUndo(null)
    const [undoTimeoutId, setUndoTimeoutId] = useState(null)

    // Color palette for teammates - theme-aware colors
    const getTeammateColors = (theme = 'light') => {
        const colorSets = {
            light: [
                { bg: '#3B82F6', text: '#FFFFFF' }, // Blue
                { bg: '#10B981', text: '#FFFFFF' }, // Emerald  
                { bg: '#F59E0B', text: '#FFFFFF' }, // Amber
                { bg: '#EF4444', text: '#FFFFFF' }, // Red
                { bg: '#8B5CF6', text: '#FFFFFF' }, // Violet
                { bg: '#06B6D4', text: '#FFFFFF' }, // Cyan
                { bg: '#84CC16', text: '#FFFFFF' }, // Lime
                { bg: '#F97316', text: '#FFFFFF' }, // Orange
                { bg: '#EC4899', text: '#FFFFFF' }, // Pink
                { bg: '#6366F1', text: '#FFFFFF' }, // Indigo
                { bg: '#14B8A6', text: '#FFFFFF' }, // Teal
                { bg: '#A855F7', text: '#FFFFFF' }  // Purple
            ],
            dark: [
                { bg: '#60A5FA', text: '#1E293B' }, // Light blue
                { bg: '#34D399', text: '#1E293B' }, // Light emerald
                { bg: '#FBBF24', text: '#1E293B' }, // Light amber
                { bg: '#F87171', text: '#1E293B' }, // Light red
                { bg: '#A78BFA', text: '#1E293B' }, // Light violet
                { bg: '#22D3EE', text: '#1E293B' }, // Light cyan
                { bg: '#A3E635', text: '#1E293B' }, // Light lime
                { bg: '#FB923C', text: '#1E293B' }, // Light orange
                { bg: '#F472B6', text: '#1E293B' }, // Light pink
                { bg: '#818CF8', text: '#1E293B' }, // Light indigo
                { bg: '#2DD4BF', text: '#1E293B' }, // Light teal
                { bg: '#C084FC', text: '#1E293B' }  // Light purple
            ],
            nightowl: [
                { bg: '#82AAFF', text: '#011627' }, // Night Owl blue
                { bg: '#ADDB67', text: '#011627' }, // Night Owl green
                { bg: '#F78C6C', text: '#011627' }, // Night Owl orange
                { bg: '#FF5370', text: '#D6DEEB' }, // Night Owl red
                { bg: '#C792EA', text: '#011627' }, // Night Owl purple
                { bg: '#80CBC4', text: '#011627' }, // Night Owl cyan
                { bg: '#7FDBCA', text: '#011627' }, // Night Owl secondary
                { bg: '#FFCB8B', text: '#011627' }, // Night Owl yellow
                { bg: '#FF9CAC', text: '#011627' }, // Night Owl pink
                { bg: '#89DDFF', text: '#011627' }, // Night Owl light blue
                { bg: '#A8E6CF', text: '#011627' }, // Night Owl mint
                { bg: '#D6ACFF', text: '#011627' }  // Night Owl lavender
            ],
            pastel: [
                { bg: '#FF9A9E', text: '#495057' }, // Pastel coral
                { bg: '#A8E6CF', text: '#495057' }, // Pastel mint
                { bg: '#FFD93D', text: '#495057' }, // Pastel yellow
                { bg: '#6C5CE7', text: '#FFFFFF' }, // Pastel purple
                { bg: '#74B9FF', text: '#495057' }, // Pastel blue
                { bg: '#FD79A8', text: '#495057' }, // Pastel pink
                { bg: '#FDCB6E', text: '#495057' }, // Pastel orange
                { bg: '#81ECEC', text: '#495057' }, // Pastel cyan
                { bg: '#FF7675', text: '#FFFFFF' }, // Pastel red
                { bg: '#55A3FF', text: '#FFFFFF' }, // Pastel indigo
                { bg: '#00B894', text: '#FFFFFF' }, // Pastel teal
                { bg: '#E84393', text: '#FFFFFF' }  // Pastel magenta
            ],
            gamer: [
                { bg: '#00FF88', text: '#0A0A0A' }, // Neon green
                { bg: '#FF0080', text: '#FFFFFF' }, // Neon pink
                { bg: '#00FFFF', text: '#0A0A0A' }, // Neon cyan
                { bg: '#FF4444', text: '#FFFFFF' }, // Neon red
                { bg: '#8A2BE2', text: '#FFFFFF' }, // Neon purple
                { bg: '#FFFF00', text: '#0A0A0A' }, // Neon yellow
                { bg: '#FF6600', text: '#0A0A0A' }, // Neon orange
                { bg: '#00AAFF', text: '#0A0A0A' }, // Neon blue
                { bg: '#FF00FF', text: '#0A0A0A' }, // Neon magenta
                { bg: '#AAFF00', text: '#0A0A0A' }, // Neon lime
                { bg: '#FF8800', text: '#0A0A0A' }, // Neon amber
                { bg: '#00FFAA', text: '#0A0A0A' }  // Neon mint
            ]
        }
        return colorSets[theme] || colorSets.dark
    }

    // Get current theme from document
    const getCurrentTheme = () => {
        return document.documentElement.getAttribute('data-theme') || 'light'
    }

    // State to track current theme for re-rendering
    const [currentTheme, setCurrentTheme] = useState(getCurrentTheme())

    const [weekDates, setWeekDates] = useState(getWeekDates(currentDate, showWeekends).map(d => d.toDate()))

    const teammateColors = getTeammateColors('pastel')

    // Watch for theme changes
    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    setCurrentTheme(getCurrentTheme())
                }
            })
        })

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        })

        return () => observer.disconnect()
    }, [])

    // Handle responsive sidebar and view mode behavior
    useEffect(() => {
        const handleResize = () => {
            const isMobileScreen = window.innerWidth <= 768
            setIsMobile(isMobileScreen)

            if (isMobileScreen) {
                // On mobile, collapse sidebar and force day view
                setSidebarCollapsed(true)
                setViewMode('day')
            } else {
                // On desktop, restore user's preferred view mode
                setViewMode(userPreferredViewMode)
            }
        }

        // Set initial state
        handleResize()

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [userPreferredViewMode])

    useEffect(() => {
        setWeekDates(getWeekDates(currentDate, showWeekends).map(d => d.toDate()))
    }, [currentDate, showWeekends])

    // Fetch teammates on mount (only for admins)
    useEffect(() => {
        const loadTeammates = async () => {
            if (!apiToken) return

            try {
                setLoadingTeammates(true)
                apiClient.setAuthToken(apiToken)

                const data = await apiClient.getTeammates()
                const teammatesWithColors = data.teammates.map((teammate, index) => ({
                    ...teammate,
                    color: teammateColors[index % teammateColors.length].bg,
                    textColor: teammateColors[index % teammateColors.length].text
                }))

                setTeammates(teammatesWithColors)
                setIsAdmin(true) // If the request succeeds, user is admin

                // Start with "My Timesheet" selected by default
                setSelectedTeammates(['self'])
            } catch (error) {
                if (error.message.includes('403') || error.message.includes('Admin access required')) {
                    setIsAdmin(false)
                    // Non-admin users only see their own data
                    setSelectedTeammates(['self'])
                } else {
                    console.error('Failed to load teammates:', error)
                }
            } finally {
                setLoadingTeammates(false)
            }
        }

        loadTeammates()
    }, [apiToken])

    // Fetch workspace users for @mentions (available to all users)
    useEffect(() => {
        const loadWorkspaceUsers = async () => {
            if (!apiToken) return

            try {
                apiClient.setAuthToken(apiToken)
                const response = await apiClient.get('/workspaces/users')
                setWorkspaceUsers(response.users || [])
            } catch (error) {
                console.error('Failed to fetch workspace users:', error)
                setWorkspaceUsers([])
            }
        }

        loadWorkspaceUsers()
    }, [apiToken])

    // Fetch projects on mount
    useEffect(() => {
        const loadProjects = async () => {
            if (!apiToken) return

            try {
                setLoadingProjects(true)
                apiClient.setAuthToken(apiToken)

                const data = await apiClient.getProjects()
                const allProjects = data.projects || []
                // Filter to only include active projects
                const activeProjects = allProjects.filter(p => p.status === 'active')
                console.log('Loaded projects:', activeProjects.length || 0)
                setProjects(activeProjects)
                setTasks(data.tasks || [])
            } catch (error) {
                console.error('Failed to load projects:', error)
                // Don't show alert for loading errors, just log them
            } finally {
                setLoadingProjects(false)
            }
        }

        loadProjects()
    }, [apiToken])

    // Check Google Calendar connection status on mount
    useEffect(() => {
        const checkGoogleCalendarConnection = async () => {
            if (!apiToken) return

            try {
                apiClient.setAuthToken(apiToken)
                const response = await apiClient.get('/google_calendar/connection_status')
                setIsGoogleCalendarConnected(response.connected || false)
                console.log('Google Calendar connected:', response.connected)
            } catch (error) {
                console.error('Failed to check Google Calendar connection:', error)
                setIsGoogleCalendarConnected(false)
            }
        }

        checkGoogleCalendarConnection()
    }, [apiToken])

    // Automatically hide calendar events when Google Calendar is disconnected
    useEffect(() => {
        if (!isGoogleCalendarConnected) {
            setIsGoogleCalendarVisible(false)
        }
    }, [isGoogleCalendarConnected])

    // Update projects when props change (for refreshing from manage page)
    useEffect(() => {
        if (availableProjects.length > 0) {
            setProjects(availableProjects)
        }
        if (availableTasks.length > 0) {
            setTasks(availableTasks)
        }
    }, [availableProjects, availableTasks])

    // Function to refresh projects (can be called when returning from manage page)
    const refreshProjects = async () => {
        if (!apiToken) return

        try {
            setLoadingProjects(true)
            apiClient.setAuthToken(apiToken)

            const data = await apiClient.getProjects()
            const allProjects = data.projects || []
            // Filter to only include active projects
            const activeProjects = allProjects.filter(p => p.status === 'active')
            console.log('Refreshed projects:', activeProjects.length || 0)
            setProjects(activeProjects)
            setTasks(data.tasks || [])
        } catch (error) {
            console.error('Failed to refresh projects:', error)
        } finally {
            setLoadingProjects(false)
        }
    }

    // Function to refresh worklogs
    const refreshWorklogs = useCallback(async () => {
        if (!apiToken) return
        if (selectedTeammates.length === 0) {
            console.log('No teammates selected, skipping worklog refresh')
            return
        }

        try {
            apiClient.setAuthToken(apiToken)

            // Get date range for the entire month to ensure monthly totals are accurate
            const monthStart = moment(currentDate).startOf('month').toDate()
            const monthEnd = moment(currentDate).endOf('month').toDate()

            // Get week range to ensure we include cross-month dates
            const currentWeekDates = getWeekDates(currentDate, showWeekends)
            const weekStart = moment(currentWeekDates[0]).startOf('day').toDate()
            const weekEnd = moment(currentWeekDates[currentWeekDates.length - 1]).endOf('day').toDate()

            // Use the broader range to ensure we have data for both monthly and weekly calculations
            const rangeStart = moment.min(moment(monthStart), moment(weekStart)).toDate()
            const rangeEnd = moment.max(moment(monthEnd), moment(weekEnd)).toDate()

            const startDate = formatDateForBackend(rangeStart)
            const endDate = formatDateForBackend(rangeEnd)

            console.log(`Refreshing worklogs for ${formatMonthYear(currentDate)}: ${startDate} to ${endDate}`)

            // Collect user IDs for multi-user totals calculation
            const userIds = selectedTeammates
                .filter(id => id !== 'self')
                .join(',')

            // Fetch worklogs WITH time totals (single call for all users)
            const params = {
                start_date: startDate,
                end_date: endDate
            }
            if (userIds) {
                params.user_id = userIds
            }

            const response = await apiClient.getWorklogsWithTotals(params)
            const calendarWorklogs = (response.worklogs || [])
                .map(mapApiWorklogToCalendarWorklog)
                .filter(Boolean)

            // Store time totals from backend (includes daily and weekly aggregates)
            setTimeTotals(response.time_totals || { daily_totals: {}, weekly_totals: {} })

            let suggestionEntries = []
            if (selectedTeammates.includes('self')) {
                const suggestionResponse = await apiClient.getWorklogSuggestions()
                suggestionEntries = (suggestionResponse.suggestions || [])
                    .map(mapSuggestionToCalendarWorklog)
                    .filter(Boolean)
            }

            console.log(`Refreshed ${calendarWorklogs.length} worklogs for ${selectedTeammates.length} teammates (plus ${suggestionEntries.length} suggestions)`)
            setWorklogs([...calendarWorklogs, ...suggestionEntries])
        } catch (error) {
            console.error('Failed to refresh worklogs:', error)
        }
    }, [apiToken, currentDate, selectedTeammates, showWeekends])

    // Function to refresh calendar events
    const refreshCalendarEvents = useCallback(async () => {
        if (!apiToken || !isGoogleCalendarConnected) return

        try {
            apiClient.setAuthToken(apiToken)

            // Define a wider fetch range for a smoother UX, fetching several weeks at once.
            // This reduces the number of API calls when navigating between adjacent weeks.
            const currentWeekDates = getWeekDates(currentDate, showWeekends);
            const weekStart = moment(currentWeekDates[0]).startOf('day');
            const weekEnd = moment(currentWeekDates[currentWeekDates.length - 1]).endOf('day');

            // Fetch a buffer of 2 weeks before and 4 weeks after the current week
            const rangeStart = weekStart.clone().subtract(2, 'weeks').toDate();
            const rangeEnd = weekEnd.clone().add(4, 'weeks').toDate();

            const startDate = formatDateForBackend(rangeStart);
            const endDate = formatDateForBackend(rangeEnd);

            console.log(`Triggering Google Calendar event fetch for range: ${startDate} to ${endDate}`);
            // First, trigger a fetch of events from Google Calendar for the given range
            await apiClient.post('/google_calendar/fetch_events', {
                start_date: startDate,
                end_date: endDate
            });

            const response = await apiClient.get('/google_calendar/events', {
                params: { start_date: startDate, end_date: endDate }
            })

            const events = (response.events || []).map(event => ({
                id: `calendar-${event.id}`,
                summary: event.summary,
                description: event.description,
                startTime: new Date(event.start_at),
                endTime: new Date(event.end_at),
                isCalendarEvent: true,
                allDay: event.all_day || false
            }))

            setCalendarEvents(events)
        } catch (error) {
            console.error('Failed to refresh calendar events:', error)
            setCalendarEvents([])
        }
    }, [apiToken, currentDate, isGoogleCalendarConnected, showWeekends])

    // Refresh projects and worklogs when window regains focus (user returns from timesheet/manage page)
    useEffect(() => {
        const handleFocus = () => {
            // Small delay to ensure any navigation has completed
            setTimeout(() => {
                refreshProjects()
                refreshWorklogs()
                refreshCalendarEvents()
            }, 500)
        }

        window.addEventListener('focus', handleFocus)
        return () => window.removeEventListener('focus', handleFocus)
    }, [refreshWorklogs, refreshCalendarEvents])

    // Refresh calendar events when calendar selection changes in Preferences
    useEffect(() => {
        const handleCalendarSelectionChanged = () => {
            console.log('Calendar selection changed, refreshing events...')
            refreshCalendarEvents()
        }

        window.addEventListener('calendarSelectionChanged', handleCalendarSelectionChanged)
        return () => window.removeEventListener('calendarSelectionChanged', handleCalendarSelectionChanged)
    }, [refreshCalendarEvents])

    // Handle Google Calendar disconnection
    useEffect(() => {
        const handleGoogleCalendarDisconnected = () => {
            console.log('Google Calendar disconnected, resetting state...')
            setIsGoogleCalendarConnected(false)
            setIsGoogleCalendarVisible(false) // Hide calendar events to revert layout
            setCalendarEvents([])
        }

        window.addEventListener('googleCalendarDisconnected', handleGoogleCalendarDisconnected)
        return () => window.removeEventListener('googleCalendarDisconnected', handleGoogleCalendarDisconnected)
    }, [])

    // Update running timer end times and date every 5 minutes
    useEffect(() => {
        const updateRunningTimers = () => {
            setWorklogs(prevWorklogs => {
                return prevWorklogs.map(worklog => {
                    if (worklog.status === 'running') {
                        return {
                            ...worklog,
                            endTime: moment().toDate(),
                            // Keep the running timer in today's column even if it started on a previous day
                            date: moment().startOf('day').toDate()
                        }
                    }
                    return worklog
                })
            })
        }

        // Update every 5 minutes (300000ms) to minimize unnecessary re-renders
        const intervalId = setInterval(updateRunningTimers, 300000)

        // Clean up on unmount
        return () => clearInterval(intervalId)
    }, [])

    // Load worklogs on mount, when date changes, or when selected teammates change
    useEffect(() => {
        refreshWorklogs()
    }, [refreshWorklogs])

    // Load calendar events on mount, when date changes, or when connection status changes
    useEffect(() => {
        refreshCalendarEvents()
    }, [refreshCalendarEvents])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (quickNavRef.current && !quickNavRef.current.contains(event.target)) {
                setShowQuickNav(false)
            }
            if (teammatesDropdownRef.current && !teammatesDropdownRef.current.contains(event.target)) {
                setShowTeammatesDropdown(false)
                setTeammateSearchTerm('') // Clear search when dropdown closes
            }

            // Close sidebar on mobile when clicking outside
            if (window.innerWidth <= 768 && !sidebarCollapsed) {
                const sidebar = document.querySelector(`.${styles.sidebar}`)
                const toggleButton = document.querySelector(`.${styles.sidebarToggle}`)
                if (sidebar && toggleButton &&
                    !sidebar.contains(event.target) &&
                    !toggleButton.contains(event.target)) {
                    setSidebarCollapsed(true)
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [sidebarCollapsed])

    // Undo functionality
    const recordAction = (actionType, data) => {
        // Clear any existing timeout
        if (undoTimeoutId) {
            clearTimeout(undoTimeoutId)
        }

        // Record the action using use-undo
        setUndoState({
            type: actionType,
            data: data,
            timestamp: Date.now()
        })

        // Set timeout to clear undo after 10 seconds
        const timeoutId = setTimeout(() => {
            resetUndo()
            setUndoTimeoutId(null)
        }, 10000)
        setUndoTimeoutId(timeoutId)
    }

    const performUndo = useCallback(async () => {
        if (!canUndo || !undoState.present || !apiToken) return

        const actionToUndo = undoState.present

        try {
            setIsLoading(true)
            setLoadingMessage('Undoing action...')
            apiClient.setAuthToken(apiToken)

            switch (actionToUndo.type) {
                case 'CREATE':
                    // Delete the created worklog
                    await apiClient.deleteWorklog(actionToUndo.data.createdId)
                    setWorklogs(prev => prev.filter(w => w.id !== actionToUndo.data.createdId))
                    break

                case 'UPDATE':
                    // Restore the original worklog
                    const originalWorklogData = actionToUndo.data.original
                    const updateData = {
                        start_time: serializeDateForApi(originalWorklogData.startTime) || originalWorklogData.start_time,
                        end_time: getEndTimeForApi(originalWorklogData),
                        description: originalWorklogData.title,
                        project: originalWorklogData.project?.id || originalWorklogData.project,
                        task: originalWorklogData.task?.id || originalWorklogData.task,
                        user_id: originalWorklogData.userId,
                        billable: originalWorklogData.billable,
                    }
                    await apiClient.updateWorklog(actionToUndo.data.worklogId, updateData)
                    // Refresh data
                    setWorklogs(prev => prev.map(w =>
                        w.id === actionToUndo.data.worklogId ? actionToUndo.data.original : w
                    ))
                    break

                case 'DELETE':
                    // Recreate the deleted worklog
                    const deletedWorklogData = actionToUndo.data.worklog
                    const recreateData = {
                        start_time: serializeDateForApi(deletedWorklogData.startTime) || deletedWorklogData.start_time,
                        end_time: getEndTimeForApi(deletedWorklogData),
                        description: deletedWorklogData.title,
                        project: deletedWorklogData.project?.id || deletedWorklogData.project,
                        task: deletedWorklogData.task?.id || deletedWorklogData.task,
                        user_id: deletedWorklogData.userId,
                        billable: deletedWorklogData.billable,
                    }
                    const response = await apiClient.createWorklog(recreateData)
                    // Handle new response format: { worklogs: [...], split: boolean }
                    const createdWorklogs = response.worklogs || [response.worklog]
                    const newWorklog = {
                        ...actionToUndo.data.worklog,
                        id: createdWorklogs[0].id
                    }
                    setWorklogs(prev => [...prev, newWorklog])
                    break

                case 'CREATE_SPLIT':
                    // Delete all created split worklogs
                    for (const id of actionToUndo.data.createdIds) {
                        await apiClient.deleteWorklog(id)
                    }
                    setWorklogs(prev => prev.filter(w => !actionToUndo.data.createdIds.includes(w.id)))
                    break

                case 'UPDATE_SPLIT':
                    // Delete the new split worklogs and recreate the original
                    for (const worklog of actionToUndo.data.newWorklogs) {
                        await apiClient.deleteWorklog(worklog.id)
                    }
                    // Recreate the original worklog
                    const originalData = actionToUndo.data.original
                    const recreateOriginal = {
                        start_time: serializeDateForApi(originalData.startTime) || originalData.start_time,
                        end_time: getEndTimeForApi(originalData),
                        description: originalData.title,
                        project: originalData.project?.id || originalData.project,
                        task: originalData.task?.id || originalData.task,
                        user_id: originalData.userId,
                        billable: originalData.billable,
                    }
                    const recreateResponse = await apiClient.createWorklog(recreateOriginal)
                    const recreatedWorklogs = recreateResponse.worklogs || [recreateResponse.worklog]
                    const restoredWorklog = {
                        ...originalData,
                        id: recreatedWorklogs[0].id
                    }
                    setWorklogs(prev => {
                        const filtered = prev.filter(w =>
                            !actionToUndo.data.newWorklogs.some(nw => nw.id === w.id)
                        )
                        return [...filtered, restoredWorklog]
                    })
                    break

                case 'SPLIT':
                    // Delete the second worklog and restore the original
                    await apiClient.deleteWorklog(actionToUndo.data.secondWorklogId)
                    const splitOriginalWorklogData = actionToUndo.data.original
                    const restoreData = {
                        start_time: serializeDateForApi(splitOriginalWorklogData.startTime) || splitOriginalWorklogData.start_time,
                        end_time: getEndTimeForApi(splitOriginalWorklogData),
                        description: splitOriginalWorklogData.title,
                        project: splitOriginalWorklogData.project?.id || splitOriginalWorklogData.project,
                        task: splitOriginalWorklogData.task?.id || splitOriginalWorklogData.task,
                        user_id: splitOriginalWorklogData.userId,
                        billable: splitOriginalWorklogData.billable,
                    }
                    await apiClient.updateWorklog(actionToUndo.data.firstWorklogId, restoreData)
                    // Update state
                    setWorklogs(prev => prev
                        .filter(w => w.id !== actionToUndo.data.secondWorklogId)
                        .map(w => w.id === actionToUndo.data.firstWorklogId ? actionToUndo.data.original : w)
                    )
                    break
            }

            // Use the library's undo function
            undo()

            // Clear the timeout
            clearTimeout(undoTimeoutId)
            setUndoTimeoutId(null)

        } catch (error) {
            console.error('Undo failed:', error)
        } finally {
            setIsLoading(false)
        }
    }, [canUndo, undoState.present, apiToken, undo, undoTimeoutId])

    // Keyboard shortcut for undo (Cmd+Z)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey && canUndo) {
                e.preventDefault()
                performUndo()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [canUndo, performUndo])

    // Handle teammate selection
    const handleTeammateToggle = (teammateId) => {
        setSelectedTeammates(prev => {
            if (prev.includes(teammateId)) {
                return prev.filter(id => id !== teammateId)
            } else {
                return [...prev, teammateId]
            }
        })
    }

    // Handle selecting none (deselect all)
    const handleSelectNone = () => {
        setSelectedTeammates([])
    }

    // Handle selecting all teammates
    const handleSelectAll = () => {
        const allTeammateIds = teammates.filter(teammate => teammate.id !== user?.id).map(teammate => teammate.id)
        setSelectedTeammates(allTeammateIds)
    }

    // Filter teammates based on search term
    const getFilteredTeammates = () => {
        if (!teammateSearchTerm) {
            return teammates.filter(teammate => teammate.id !== user?.id)
        }

        const searchLower = teammateSearchTerm.toLowerCase()
        return teammates.filter(teammate =>
            teammate.id !== user?.id &&
            (teammate.display_name?.toLowerCase().includes(searchLower) ||
                teammate.email?.toLowerCase().includes(searchLower) ||
                teammate.first_name?.toLowerCase().includes(searchLower) ||
                teammate.last_name?.toLowerCase().includes(searchLower))
        )
    }

    const getWorklogColors = (teammateId) => {
        const cardColors = {
            light: { cardColor: 'rgba(15, 23, 42, 0.8)', textColor: '#ffffff' },
            dark: { cardColor: '#0f172a', textColor: '#e2e8f0' },
            nightowl: { cardColor: '#112240', textColor: '#d6deeb' },
            pastel: { cardColor: '#ffe8d6', textColor: '#495057' },
            gamer: { cardColor: '#0d1117', textColor: '#c9d1d9' }
        }

        const themeColors = cardColors[currentTheme] || cardColors.dark

        if (teammateId === 'self') {
            const selfBorderColors = {
                light: '#1f2937',
                dark: '#059669',
                nightowl: '#7c3aed',
                pastel: '#212529',
                gamer: '#00ff41'
            }
            // Keep current user cards with dark background
            return {
                cardColor: themeColors.cardColor,
                textColor: themeColors.textColor,
                borderColor: selfBorderColors[currentTheme] || selfBorderColors.dark
            }
        }

        // For teammates, use pastel background colors
        const teammate = teammates.find(t => t.id === teammateId)
        if (teammate) {
            // Use the teammate's assigned pastel color as background
            return {
                cardColor: teammate.color || '#FFB6C1', // Fallback to light pink
                textColor: teammate.textColor || '#495057', // Use assigned text color or dark fallback
                borderColor: teammate.color || '#FFB6C1'
            }
        }

        // Fallback for unknown teammates
        return {
            cardColor: '#FFB6C1', // Light pink fallback
            textColor: '#495057', // Dark text for good contrast
            borderColor: '#FFB6C1'
        }
    }

    const handleDateChange = (date) => {
        // For week navigation (e.g., from mini calendar), update URL with week parameter
        if (viewMode === 'week') {
            const monday = getMondayOfWeek(date)
            router.get(window.location.pathname, { week: monday }, {
                preserveScroll: true,
                preserveState: true,
                only: []
            })
        }
        setCurrentDate(date)
        setSelectedDate(date)
    }

    const handleMonthChange = (date) => {
        setCurrentDate(date)
    }

    const navigateWeek = (direction) => {
        const daysToAdd = viewMode === 'day' ? direction : direction * 7
        const newDate = moment(currentDate).add(daysToAdd, 'days').toDate()
        const monday = getMondayOfWeek(newDate)

        // Update URL with week parameter via Inertia router
        router.get(window.location.pathname, { week: monday }, {
            preserveScroll: true,
            preserveState: true,
            only: [] // Don't need to refresh props
        })

        setCurrentDate(newDate)
    }

    const handleWorklogCreate = async (worklog) => {
        try {
            setIsLoading(true)
            setLoadingMessage('Creating worklog...')

            console.log('=== handleWorklogCreate START ===')
            console.log('Input worklog:', worklog)
            console.log('worklog.userId:', worklog.userId)
            console.log('user?.id (fallback):', user?.id)
            console.log('Current worklogs before create:', worklogs.length)

            // Prepare worklog data for API
            const worklogData = {
                user_id: worklog.userId || user?.id,
                start_time: serializeDateForApi(worklog.startTime) || worklog.start_time,
                end_time: getEndTimeForApi(worklog, worklog.status),
                description: worklog.title,
                project: worklog.project,
                task: worklog.task,
                billable: worklog.billable,
            }

            console.log('Final worklogData.user_id:', worklogData.user_id)
            console.log('Prepared worklogData for API:', worklogData)

            // Call API to create worklog
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.createWorklog(worklogData)

            // Handle both single worklog and split worklogs
            const worklogs_to_add = response.worklogs || [response.worklog]
            const was_split = response.split || false

            // Convert each API worklog to calendar format
            const calendarWorklogs = worklogs_to_add.map(apiWorklog => {
                const responseStartTime = moment(apiWorklog.start_time).toDate()
                // For running timers (status: 'running'), use current time instead of null end_time
                const responseEndTime = apiWorklog.status === 'running'
                    ? moment().toDate()
                    : moment(apiWorklog.end_time).toDate()
                // For running timers, use today's date so the timer appears in the current day's column.
                // For completed worklogs, use logged_date from backend (important for split worklogs across days).
                const responseWorklogDate = apiWorklog.status === 'running'
                    ? moment().startOf('day').toDate()
                    : apiWorklog.logged_date
                        ? moment(apiWorklog.logged_date).toDate()
                        : moment(apiWorklog.start_time).startOf('day').toDate()

                return {
                    id: apiWorklog.id,
                    title: apiWorklog.description || apiWorklog.title,
                    startTime: responseStartTime,
                    endTime: responseEndTime,
                    date: responseWorklogDate,
                    project: apiWorklog.project || { id: apiWorklog.project_id || 'General', name: apiWorklog.project_name || 'General' },
                    projectName: apiWorklog.project_name || apiWorklog.task_name || 'General',
                    task: apiWorklog.task_id ? {
                        id: apiWorklog.task_id,
                        name: apiWorklog.task_name
                    } : null,
                    userId: apiWorklog.user_id,
                    billable: apiWorklog.billable,
                    status: apiWorklog.status,
                    type: 'work'
                }
            })

            console.log('Final calendarWorklogs to add:', calendarWorklogs)
            console.log('Before setWorklogs - current state:', worklogs.map(w => ({ id: w.id, title: w.title })))

            // Add all worklogs to state
            setWorklogs(prevWorklogs => {
                const newWorklogs = [...prevWorklogs, ...calendarWorklogs]
                console.log('setWorklogs called - new state will be:', newWorklogs.map(w => ({ id: w.id, title: w.title })))
                return newWorklogs
            })

            // Record action for undo (handle multiple worklogs)
            if (calendarWorklogs.length === 1) {
                recordAction('CREATE', {
                    createdId: calendarWorklogs[0].id,
                    worklog: calendarWorklogs[0]
                })
            } else {
                // For split worklogs, record both
                recordAction('CREATE_SPLIT', {
                    createdIds: calendarWorklogs.map(w => w.id),
                    worklogs: calendarWorklogs
                })
            }

            // Show success message for split worklogs
            console.log('Split check:', { was_split, worklogCount: calendarWorklogs.length })
            if (was_split) {
                console.log('Showing split notification')
                setSuccessMessage('Entry crossed midnight - created 2 separate time entries')
                setTimeout(() => setSuccessMessage(null), 5000)
            }

            console.log('=== handleWorklogCreate END ===')

            // Auto-check teammate if worklog was created for someone other than current user
            const assignedUserId = worklog.userId || user?.id
            if (isAdmin && assignedUserId && assignedUserId !== user?.id) {
                if (!selectedTeammates.includes(assignedUserId)) {
                    setSelectedTeammates(prev => [...prev, assignedUserId])
                }
            }
        } catch (error) {
            console.error('Failed to create worklog:', error)
            alert('Failed to create worklog: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleWorklogUpdate = async (id, updatedWorklog) => {
        try {
            setIsLoading(true)
            setLoadingMessage('Updating worklog...')

            console.log('=== handleWorklogUpdate START ===')
            console.log('Updating worklog ID:', id)
            console.log('Update data received:', updatedWorklog)
            console.log('Current admin user ID:', user?.id)

            // Capture original worklog for undo
            const originalWorklog = worklogs.find(w => w.id === id)

            // Convert frontend format to API format
            const targetStatus = updatedWorklog.status || originalWorklog?.status || null

            const updateData = {
                user_id: updatedWorklog.user_id || updatedWorklog.userId || user?.id,
                start_time: serializeDateForApi(updatedWorklog.startTime) || updatedWorklog.start_time,
                end_time: getEndTimeForApi(updatedWorklog, targetStatus),
                description: updatedWorklog.title || updatedWorklog.description,
                project: updatedWorklog.project,
                task: updatedWorklog.task,
                billable: updatedWorklog.billable,
            }

            console.log('Final updateData being sent to API:', updateData)
            console.log('User ID chain: updatedWorklog.user_id =', updatedWorklog.user_id, ', updatedWorklog.userId =', updatedWorklog.userId, ', fallback user?.id =', user?.id)

            // Call API to update worklog
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.updateWorklog(id, updateData)

            // Handle both single worklog and split worklogs
            const worklogs_to_update = response.worklogs || [response.worklog]
            const was_split = response.split || false

            // Convert each API worklog to calendar format
            const updatedCalendarWorklogs = worklogs_to_update.map(apiWorklog => {
                const updateStartTime = moment(apiWorklog.start_time).toDate()
                // For running timers (status: 'running'), use current time instead of null end_time
                const updateEndTime = apiWorklog.status === 'running'
                    ? moment().toDate()
                    : moment(apiWorklog.end_time).toDate()
                // For running timers, use today's date so the timer appears in the current day's column.
                // For completed worklogs, use logged_date from backend (important for split worklogs across days).
                const updateWorklogDate = apiWorklog.status === 'running'
                    ? moment().startOf('day').toDate()
                    : apiWorklog.logged_date
                        ? moment(apiWorklog.logged_date).toDate()
                        : moment(apiWorklog.start_time).startOf('day').toDate()

                return {
                    id: apiWorklog.id,
                    title: apiWorklog.description || apiWorklog.title,
                    startTime: updateStartTime,
                    endTime: updateEndTime,
                    date: updateWorklogDate,
                    project: apiWorklog.project || { id: apiWorklog.project_id || 'General', name: apiWorklog.project_name || 'General' },
                    projectName: apiWorklog.project_name || apiWorklog.task_name || 'General',
                    task: apiWorklog.task_id ? {
                        id: apiWorklog.task_id,
                        name: apiWorklog.task_name
                    } : null,
                    userId: apiWorklog.user_id,
                    billable: apiWorklog.billable,
                    status: apiWorklog.status,
                    type: 'work'
                }
            })

            setWorklogs(prevWorklogs => {
                if (was_split) {
                    // Remove old worklog and add both new split worklogs
                    const filteredWorklogs = prevWorklogs.filter(w => w.id !== id)
                    const newWorklogs = [...filteredWorklogs, ...updatedCalendarWorklogs]
                    console.log('handleWorklogUpdate - split update:', {
                        removedId: id,
                        addedIds: updatedCalendarWorklogs.map(w => w.id),
                        totalCount: newWorklogs.length
                    })
                    return newWorklogs
                } else {
                    // Normal update - replace the worklog
                    const newWorklogs = prevWorklogs.map(w => w.id === id ? updatedCalendarWorklogs[0] : w)
                    console.log('handleWorklogUpdate - normal update:', {
                        oldWorklog: prevWorklogs.find(w => w.id === id),
                        newWorklog: updatedCalendarWorklogs[0],
                        totalCount: newWorklogs.length
                    })
                    return newWorklogs
                }
            })

            // Record action for undo
            if (originalWorklog) {
                if (was_split) {
                    recordAction('UPDATE_SPLIT', {
                        originalId: id,
                        original: originalWorklog,
                        newWorklogs: updatedCalendarWorklogs
                    })
                } else {
                    recordAction('UPDATE', {
                        worklogId: id,
                        original: originalWorklog,
                        updated: updatedCalendarWorklogs[0]
                    })
                }
            }

            // Show success message for split worklogs
            console.log('Update split check:', { was_split, worklogCount: updatedCalendarWorklogs.length })
            if (was_split) {
                console.log('Showing split notification for update')
                setSuccessMessage('Entry crossed midnight - updated to 2 separate time entries')
                setTimeout(() => setSuccessMessage(null), 5000)
            }

            console.log('=== handleWorklogUpdate END ===')
        } catch (error) {
            console.error('Failed to update worklog:', error)
            alert('Failed to update worklog: ' + error.message)

            // Reload worklogs to revert any optimistic updates
            // This could be improved with proper error handling and rollback
            const currentMonth = moment(currentDate).startOf('month').toDate()
            // Trigger a reload by updating a dependency or calling loadWorklogs directly
        } finally {
            setIsLoading(false)
        }
    }

    const handleWorklogDelete = async (id) => {
        try {
            setIsLoading(true)
            setLoadingMessage('Deleting worklog...')

            // Capture worklog for undo before deleting
            const worklogToDelete = worklogs.find(w => w.id === id)

            // Call API to delete worklog
            apiClient.setAuthToken(apiToken)
            await apiClient.deleteWorklog(id)

            // Remove from local state
            setWorklogs(worklogs.filter(w => w.id !== id))

            // Record action for undo
            if (worklogToDelete) {
                recordAction('DELETE', {
                    worklog: worklogToDelete
                })
            }
        } catch (error) {
            console.error('Failed to delete worklog:', error)
            alert('Failed to delete worklog: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleSuggestionAccept = async (suggestionWorklog) => {
        if (!apiToken || !suggestionWorklog?.suggestionId) return

        try {
            setIsLoading(true)
            setLoadingMessage('Accepting pairing suggestion...')
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.acceptWorklogSuggestion(suggestionWorklog.suggestionId)

            const createdCalendarWorklog = mapApiWorklogToCalendarWorklog(response.worklog)
            if (createdCalendarWorklog) {
                recordAction('CREATE', {
                    createdId: createdCalendarWorklog.id,
                    worklog: createdCalendarWorklog
                })
            }

            await refreshWorklogs()
        } catch (error) {
            console.error('Failed to accept suggestion:', error)
            alert('Failed to accept suggestion: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleSuggestionDelete = async (suggestionWorklog) => {
        if (!apiToken || !suggestionWorklog?.suggestionId) return

        try {
            setIsLoading(true)
            setLoadingMessage('Removing pairing suggestion...')
            apiClient.setAuthToken(apiToken)
            await apiClient.deleteWorklogSuggestion(suggestionWorklog.suggestionId)

            await refreshWorklogs()
        } catch (error) {
            console.error('Failed to remove suggestion:', error)
            alert('Failed to remove suggestion: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    // Quick navigation functions
    const handleQuickNavigation = (option) => {
        const today = moment().toDate()
        let targetDate = today
        let label = 'Today'

        switch (option) {
            case 'today':
                targetDate = today
                label = 'Today'
                break
            case 'yesterday':
                targetDate = moment().subtract(1, 'day').toDate()
                label = 'Yesterday'
                break
            case 'lastWeek':
                targetDate = moment().subtract(7, 'days').toDate()
                label = 'Last Week'
                break
            default:
                targetDate = today
                label = 'Today'
        }

        // For week navigation, update URL with week parameter
        if (viewMode === 'week') {
            const monday = getMondayOfWeek(targetDate)
            router.get(window.location.pathname, { week: monday }, {
                preserveScroll: true,
                preserveState: true,
                only: []
            })
        }

        setSelectedQuickNavLabel(label)
        handleDateChange(targetDate)
        setShowQuickNav(false)
    }

    // Helper function to get teammate display name
    const getTeammateDisplayName = (userId) => {
        if (userId === user?.id) return 'You'
        const teammate = teammates.find(t => t.id === userId)
        return teammate?.display_name || teammate?.first_name || 'Unknown User'
    }

    const mapApiWorklogToCalendarWorklog = (apiWorklog) => {
        if (!apiWorklog) return null

        const startTime = moment(apiWorklog.start_time).toDate()
        const endTime = apiWorklog.status === 'running'
            ? moment().toDate()
            : moment(apiWorklog.end_time).toDate()
        // For running timers, use today's date so the timer appears in the current day's column
        // instead of the day it was originally started on
        const worklogDate = apiWorklog.status === 'running'
            ? moment().startOf('day').toDate()
            : moment(apiWorklog.start_time).startOf('day').toDate()

        return {
            id: apiWorklog.id,
            title: apiWorklog.description || apiWorklog.title,
            startTime,
            endTime,
            date: worklogDate,
            project: apiWorklog.project || { id: apiWorklog.project_id || 'General', name: apiWorklog.project_name || 'General' },
            projectName: apiWorklog.project_name || apiWorklog.task_name || 'General',
            task: apiWorklog.task_id ? {
                id: apiWorklog.task_id,
                name: apiWorklog.task_name
            } : (apiWorklog.task_name ? { id: apiWorklog.task_id, name: apiWorklog.task_name } : null),
            userId: apiWorklog.user_id,
            billable: apiWorklog.billable,
            status: apiWorklog.status,
            type: 'work'
        }
    }

    const mapSuggestionToCalendarWorklog = (suggestion) => {
        if (!suggestion?.payload) return null

        const payload = suggestion.payload
        if (!payload.start_time) return null

        const startTime = moment(payload.start_time).toDate()
        const endTime = payload.end_time
            ? moment(payload.end_time).toDate()
            : payload.duration_seconds
                ? moment(payload.start_time).add(payload.duration_seconds, 'seconds').toDate()
                : moment(payload.start_time).add(30, 'minutes').toDate()

        return {
            id: `suggestion-${suggestion.id}`,
            suggestionId: suggestion.id,
            title: payload.description || 'Pair suggestion',
            startTime,
            endTime,
            date: moment(payload.start_time).startOf('day').toDate(),
            project: payload.project_id ? {
                id: payload.project_id,
                name: payload.project_name || 'Project'
            } : null,
            task: payload.task_id ? {
                id: payload.task_id,
                name: payload.task_name || 'Task'
            } : null,
            userId: user?.id,
            billable: payload.billable,
            status: 'suggested',
            suggestedBy: suggestion.suggested_by,
            type: 'suggestion'
        }
    }

    // Helper function to filter worklogs by selected teammates
    const getVisibleWorklogs = (worklogList) => {
        return worklogList.filter(worklog => {
            // Show worklog if user is selected or if it's current user's worklog and 'self' is selected
            const isCurrentUserWorklog = worklog.userId === user?.id
            const isUserSelected = selectedTeammates.includes(worklog.userId)
            const isSelfSelected = selectedTeammates.includes('self') && isCurrentUserWorklog

            return isUserSelected || isSelfSelected
        })
    }

    const serializeDateForApi = (value) => {
        if (!value) return null
        return value instanceof Date ? value.toISOString() : value
    }

    const getEndTimeForApi = (worklog, fallbackStatus = null) => {
        if (!worklog) return null
        const status = worklog.status || fallbackStatus
        if (status === 'running') {
            return null
        }
        const endTimeValue = worklog.endTime ?? worklog.end_time ?? null
        return serializeDateForApi(endTimeValue)
    }

    // Calculate time totals
    const calculateDuration = (startTime, endTime) => {
        return moment(endTime).diff(moment(startTime), 'hours', true) // hours with decimals
    }

    // Helper to convert HH:MM:SS time string to decimal hours
    const parseTimeStringToHours = (timeString) => {
        if (!timeString) return 0
        const [hours, minutes, seconds] = timeString.split(':').map(Number)
        return hours + (minutes / 60) + (seconds / 3600)
    }

    // Helper to format week range (matches backend format: "Dec 2 - Dec 8")
    const formatWeekRange = (startDate, endDate) => {
        const start = moment(startDate)
        const end = moment(endDate)
        return `${start.format('MMM D')} - ${end.format('MMM D')}`
    }

    const getWeeklyTotals = () => {
        const weekStart = moment(weekDates[0]).startOf('day').toDate()
        const weekEnd = moment(weekDates[weekDates.length - 1]).endOf('day').toDate()

        // Use backend-calculated totals if available
        if (timeTotals && timeTotals.weekly_totals) {
            const weekKey = formatWeekRange(weekStart, weekEnd)
            const backendTotal = timeTotals.weekly_totals[weekKey]

            if (backendTotal) {
                // Convert HH:MM:SS to decimal hours
                const totalHours = parseTimeStringToHours(backendTotal)

                // Calculate project breakdown from loaded worklogs (for UI display)
                const weeklyWorklogs = worklogs.filter(worklog => {
                    const worklogDate = moment(worklog.date).startOf('day')
                    const weekStartMoment = moment(weekStart)
                    const weekEndMoment = moment(weekEnd)
                    return worklogDate.isBetween(weekStartMoment, weekEndMoment, 'day', '[]')
                })

                const visibleWeeklyWorklogs = getVisibleWorklogs(weeklyWorklogs)

                const projectTotals = visibleWeeklyWorklogs.reduce((acc, worklog) => {
                    const projectName = worklog.projectName || 'General'
                    const projectId = worklog.project?.id || 'general'
                    const projectColor = worklog.project?.color || '#6B7280'
                    const duration = calculateDuration(worklog.startTime, worklog.endTime)

                    if (!acc[projectName]) {
                        acc[projectName] = {
                            hours: 0,
                            color: projectColor,
                            name: projectName,
                            id: projectId
                        }
                    }
                    acc[projectName].hours += duration
                    return acc
                }, {})

                return { totalHours, projectTotals }
            }
        }

        // Fallback to client-side calculation if backend totals not available
        console.warn('Backend totals not available, falling back to client-side calculation')
        const weeklyWorklogs = worklogs.filter(worklog => {
            const worklogDate = moment(worklog.date).startOf('day')
            const weekStartMoment = moment(weekStart)
            const weekEndMoment = moment(weekEnd)
            return worklogDate.isBetween(weekStartMoment, weekEndMoment, 'day', '[]')
        })

        const visibleWeeklyWorklogs = getVisibleWorklogs(weeklyWorklogs)

        const totalHours = visibleWeeklyWorklogs.reduce((total, worklog) => {
            return total + calculateDuration(worklog.startTime, worklog.endTime)
        }, 0)

        const projectTotals = visibleWeeklyWorklogs.reduce((acc, worklog) => {
            const projectName = worklog.projectName || 'General'
            const projectId = worklog.project?.id || 'general'
            const projectColor = worklog.project?.color || '#6B7280'
            const duration = calculateDuration(worklog.startTime, worklog.endTime)

            if (!acc[projectName]) {
                acc[projectName] = {
                    hours: 0,
                    color: projectColor,
                    name: projectName,
                    id: projectId
                }
            }
            acc[projectName].hours += duration
            return acc
        }, {})

        return { totalHours, projectTotals }
    }

    const getMonthlyTotals = () => {
        const monthStart = moment(currentDate).startOf('month').toDate()
        const monthEnd = moment(currentDate).endOf('month').toDate()

        // Filter by date range first
        const monthlyWorklogs = worklogs.filter(worklog => {
            const worklogDate = moment(worklog.date).startOf('day')
            const monthStartMoment = moment(monthStart)
            const monthEndMoment = moment(monthEnd)
            return worklogDate.isBetween(monthStartMoment, monthEndMoment, 'day', '[]')
        })

        // Then filter by selected teammates
        const visibleMonthlyWorklogs = getVisibleWorklogs(monthlyWorklogs)

        const totalHours = visibleMonthlyWorklogs.reduce((total, worklog) => {
            return total + calculateDuration(worklog.startTime, worklog.endTime)
        }, 0)

        // Group by project with color information
        const projectTotals = visibleMonthlyWorklogs.reduce((acc, worklog) => {
            const projectName = worklog.projectName || 'General'
            const projectId = worklog.project?.id || 'general'
            const projectColor = worklog.project?.color || '#6B7280' // Default gray color
            const duration = calculateDuration(worklog.startTime, worklog.endTime)

            if (!acc[projectName]) {
                acc[projectName] = {
                    hours: 0,
                    color: projectColor,
                    name: projectName,
                    id: projectId
                }
            }
            acc[projectName].hours += duration
            return acc
        }, {})

        return { totalHours, projectTotals }
    }

    const formatHours = (hours) => {
        const h = Math.floor(hours)
        const m = Math.round((hours - h) * 60)
        if (h === 0) return `${m}m`
        if (m === 0) return `${h}h`
        return `${h}h ${m}m`
    }

    const weeklyTotals = getWeeklyTotals()
    const monthlyTotals = getMonthlyTotals()

    return (
        <div className={styles.calendarContainer}>
            {/* Loading Overlay */}
            <LoadingOverlay isLoading={isLoading} message={loadingMessage} />

            {/* Success Message */}
            {successMessage && (
                <div className={styles.successMessage}>
                    <span>{successMessage}</span>
                    <button
                        className={styles.closeSuccessButton}
                        onClick={() => setSuccessMessage(null)}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Sidebar */}
            <div className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
                <MiniCalendar
                    currentDate={currentDate}
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    onMonthChange={handleMonthChange}
                    worklogs={worklogs}
                    selectedTeammates={selectedTeammates}
                    currentUser={user}
                />

                {/* Time Totals */}
                <div className={styles.totals}>
                    {/* Weekly Total */}
                    <div className={styles.totalSection}>
                        <button
                            className={styles.totalHeader}
                            onClick={() => setWeeklyExpanded(!weeklyExpanded)}
                        >
                            <div className={styles.totalTitle}>
                                <span>Weekly Total</span>
                                <span className={styles.totalValue}>{formatHours(weeklyTotals.totalHours)}</span>
                            </div>
                            <span className={`${styles.chevron} ${weeklyExpanded ? styles.expanded : ''}`}>
                                ›
                            </span>
                        </button>
                        {weeklyExpanded && (
                            <div className={styles.projectBreakdown}>
                                {Object.entries(weeklyTotals.projectTotals).map(([project, data]) => (
                                    <div
                                        key={`weekly-${project}`}
                                        className={styles.projectItem}
                                        onMouseEnter={() => setHoveredProjectId(data.id)}
                                        onMouseLeave={() => setHoveredProjectId(null)}
                                    >
                                        <div className={styles.projectInfo}>
                                            <span
                                                className={styles.projectColor}
                                                style={{ backgroundColor: data.color }}
                                            ></span>
                                            <span className={styles.projectName}>{data.name}</span>
                                        </div>
                                        <span className={styles.projectHours}>{formatHours(data.hours)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Monthly Total */}
                    <div className={styles.totalSection}>
                        <button
                            className={styles.totalHeader}
                            onClick={() => setMonthlyExpanded(!monthlyExpanded)}
                        >
                            <div className={styles.totalTitle}>
                                <span>Monthly Total</span>
                                <span className={styles.totalValue}>{formatHours(monthlyTotals.totalHours)}</span>
                            </div>
                            <span className={`${styles.chevron} ${monthlyExpanded ? styles.expanded : ''}`}>
                                ›
                            </span>
                        </button>
                        {monthlyExpanded && (
                            <div className={styles.projectBreakdown}>
                                {Object.entries(monthlyTotals.projectTotals).map(([project, data]) => (
                                    <div
                                        key={`monthly-${project}`}
                                        className={styles.projectItem}
                                        onMouseEnter={() => setHoveredProjectId(data.id)}
                                        onMouseLeave={() => setHoveredProjectId(null)}
                                    >
                                        <div className={styles.projectInfo}>
                                            <span
                                                className={styles.projectColor}
                                                style={{ backgroundColor: data.color }}
                                            ></span>
                                            <span className={styles.projectName}>{data.name}</span>
                                        </div>
                                        <span className={styles.projectHours}>{formatHours(data.hours)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Sidebar Toggle Button */}
            <button
                className={styles.sidebarToggle}
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
                {sidebarCollapsed ? '›' : '‹'}
            </button>

            {/* Main Calendar View */}
            <div className={`${styles.mainView} ${sidebarCollapsed ? styles.expanded : ''}`}>
                {/* Header with month and navigation */}
                <div className={styles.calendarHeader}>
                    <div className={styles.monthInfo}>
                        <h2>{formatMonthYear(currentDate)}</h2>
                        <div className={styles.weekNavigation}>
                            <button
                                className={styles.navButton}
                                onClick={() => navigateWeek(-1)}
                                aria-label={`Previous ${viewMode}`}
                            >
                                ‹
                            </button>
                            <button
                                className={styles.navButton}
                                onClick={() => navigateWeek(1)}
                                aria-label={`Next ${viewMode}`}
                            >
                                ›
                            </button>
                        </div>
                    </div>
                    <div className={styles.headerControls}>
                        {/* View Density Switcher */}
                        <div className={styles.viewDensitySwitcher}>
                            <button
                                className={`${styles.densityButton} ${calendarViewDensity === 'compact' ? styles.active : ''}`}
                                onClick={() => setCalendarViewDensity('compact')}
                                title="Compact view"
                                aria-label="Switch to compact view"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="3" y1="6" x2="21" y2="6"></line>
                                    <line x1="3" y1="12" x2="21" y2="12"></line>
                                    <line x1="3" y1="18" x2="21" y2="18"></line>
                                </svg>
                            </button>
                            <button
                                className={`${styles.densityButton} ${calendarViewDensity === 'relaxed' ? styles.active : ''}`}
                                onClick={() => setCalendarViewDensity('relaxed')}
                                title="Relaxed view"
                                aria-label="Switch to relaxed view"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="3" y1="4" x2="21" y2="4"></line>
                                    <line x1="3" y1="12" x2="21" y2="12"></line>
                                    <line x1="3" y1="20" x2="21" y2="20"></line>
                                </svg>
                            </button>
                        </div>

                        {/* Teammates Dropdown (Admin only) */}
                        {isAdmin && (
                            <div className={styles.teammatesDropdown} ref={teammatesDropdownRef}>
                                <button
                                    className={styles.teammatesButton}
                                    onClick={() => setShowTeammatesDropdown(!showTeammatesDropdown)}
                                    aria-haspopup="true"
                                    aria-expanded={showTeammatesDropdown}
                                    disabled={loadingTeammates}
                                >
                                    {loadingTeammates ? 'Loading...' : 'Teammates'}
                                    <span className={`${styles.dropdownChevron} ${showTeammatesDropdown ? styles.expanded : ''}`}>
                                        ▼
                                    </span>
                                </button>

                                {showTeammatesDropdown && (
                                    <div className={styles.teammatesDropdownMenu}>
                                        <div className={styles.teammatesHeader}>
                                            <span>Select teammates to view</span>
                                        </div>

                                        {/* Search box */}
                                        <div className={styles.teammateSearchBox}>
                                            <input
                                                type="text"
                                                placeholder="Search teammates..."
                                                value={teammateSearchTerm}
                                                onChange={(e) => setTeammateSearchTerm(e.target.value)}
                                                className={styles.teammateSearchInput}
                                                onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                                            />
                                        </div>

                                        {/* Action buttons */}
                                        <div className={styles.teammateActionButtons}>
                                            <button
                                                type="button"
                                                onClick={handleSelectAll}
                                                className={`${styles.teammateActionButton} ${styles.teammateSelectAllButton}`}
                                            >
                                                Select All ({teammates.filter(teammate => teammate.id !== user?.id).length})
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSelectNone}
                                                className={`${styles.teammateActionButton} ${styles.teammateClearButton}`}
                                            >
                                                Clear ({selectedTeammates.length})
                                            </button>
                                        </div>

                                        {/* My Timesheet option - show if no search or matches search */}
                                        {(!teammateSearchTerm ||
                                            `${user?.first_name} ${user?.last_name}`.toLowerCase().includes(teammateSearchTerm.toLowerCase()) ||
                                            user?.email?.toLowerCase().includes(teammateSearchTerm.toLowerCase()) ||
                                            'my timesheet'.includes(teammateSearchTerm.toLowerCase())) && (
                                                <label className={styles.teammateItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTeammates.includes('self')}
                                                        onChange={() => handleTeammateToggle('self')}
                                                        className={styles.teammateCheckbox}
                                                    />
                                                    <div className={styles.teammateInfo}>
                                                        <span
                                                            className={styles.teammateColor}
                                                            style={{ backgroundColor: getWorklogColors('self').cardColor }}
                                                        ></span>
                                                        <div className={styles.teammateDetails}>
                                                            <span className={styles.teammateName}>My Timesheet ({user?.first_name} {user?.last_name})</span>
                                                            <span className={styles.teammateEmail}>{user?.email}</span>
                                                        </div>
                                                    </div>
                                                </label>
                                            )}

                                        {/* Teammates list - filtered */}
                                        {getFilteredTeammates().map(teammate => (
                                            <label key={teammate.id} className={styles.teammateItem}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTeammates.includes(teammate.id)}
                                                    onChange={() => handleTeammateToggle(teammate.id)}
                                                    className={styles.teammateCheckbox}
                                                />
                                                <div className={styles.teammateInfo}>
                                                    <span
                                                        className={styles.teammateColor}
                                                        style={{ backgroundColor: teammate.color }}
                                                    ></span>
                                                    <div className={styles.teammateDetails}>
                                                        <span className={styles.teammateName}>{teammate.display_name}</span>
                                                        <span className={styles.teammateEmail}>{teammate.email}</span>
                                                    </div>
                                                </div>
                                            </label>
                                        ))}

                                        {/* No results message */}
                                        {teammateSearchTerm && getFilteredTeammates().length === 0 &&
                                            !`${user?.first_name} ${user?.last_name}`.toLowerCase().includes(teammateSearchTerm.toLowerCase()) &&
                                            !user?.email?.toLowerCase().includes(teammateSearchTerm.toLowerCase()) &&
                                            !'my timesheet'.includes(teammateSearchTerm.toLowerCase()) && (
                                                <div className={styles.noResultsMessage}>
                                                    No teammates found matching "{teammateSearchTerm}"
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Google Calendar Toggle - Only show if workspace has access to the feature */}
                        {isGoogleCalendarConnected && isFeatureAvailable('google_calendar_integration', currentWorkspace) && (
                            <div className={styles.googleCalendarToggle}>
                                <label className={styles.toggleLabel}>
                                    <input
                                        type="checkbox"
                                        checked={isGoogleCalendarVisible}
                                        onChange={(e) => setIsGoogleCalendarVisible(e.target.checked)}
                                        className={styles.toggleCheckbox}
                                    />
                                    <span className={styles.toggleSlider}></span>
                                    <span className={styles.toggleText}>Google Calendar</span>
                                </label>
                            </div>
                        )}

                        {/* Weekend Toggle (Weekly view only) */}
                        {viewMode === 'week' && (
                            <div className={styles.weekendToggle}>
                                <label className={styles.toggleLabel}>
                                    <input
                                        type="checkbox"
                                        checked={showWeekends}
                                        onChange={(e) => setShowWeekends(e.target.checked)}
                                        className={styles.toggleCheckbox}
                                    />
                                    <span className={styles.toggleSlider}></span>
                                    <span className={styles.toggleText}>Show Weekends</span>
                                </label>
                            </div>
                        )}

                        {/* View Switcher - Hidden on mobile */}
                        {!isMobile && (
                            <div className={styles.viewSwitcher}>
                                <button
                                    className={`${styles.viewButton} ${viewMode === 'day' ? styles.active : ''}`}
                                    onClick={() => {
                                        setViewMode('day')
                                        setUserPreferredViewMode('day')
                                    }}
                                >
                                    Day
                                </button>
                                <button
                                    className={`${styles.viewButton} ${viewMode === 'week' ? styles.active : ''}`}
                                    onClick={() => {
                                        setViewMode('week')
                                        setUserPreferredViewMode('week')
                                    }}
                                >
                                    Week
                                </button>
                            </div>
                        )}

                        {/* Quick Navigation Dropdown */}
                        <div className={styles.quickNavigation} ref={quickNavRef}>
                            <button
                                className={styles.todayButton}
                                onClick={() => setShowQuickNav(!showQuickNav)}
                                aria-haspopup="true"
                                aria-expanded={showQuickNav}
                            >
                                {selectedQuickNavLabel}
                                <span className={`${styles.dropdownChevron} ${showQuickNav ? styles.expanded : ''}`}>
                                    ▼
                                </span>
                            </button>

                            {showQuickNav && (
                                <div className={styles.quickNavDropdown}>
                                    <button
                                        className={styles.quickNavItem}
                                        onClick={() => handleQuickNavigation('today')}
                                    >
                                        Today
                                    </button>
                                    <button
                                        className={styles.quickNavItem}
                                        onClick={() => handleQuickNavigation('yesterday')}
                                    >
                                        Yesterday
                                    </button>
                                    <button
                                        className={styles.quickNavItem}
                                        onClick={() => handleQuickNavigation('lastWeek')}
                                    >
                                        Last Week
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Calendar View - Day or Week */}
                {viewMode === 'week' ? (
                    <WeeklyView
                        weekDates={weekDates}
                        selectedDate={selectedDate}
                        worklogs={worklogs}
                        calendarEvents={isGoogleCalendarVisible ? calendarEvents : []}
                        isGoogleCalendarVisible={isGoogleCalendarVisible}
                        onWorklogCreate={handleWorklogCreate}
                        onWorklogUpdate={handleWorklogUpdate}
                        onWorklogDelete={handleWorklogDelete}
                        onSuggestionAccept={handleSuggestionAccept}
                        onSuggestionDelete={handleSuggestionDelete}
                        selectedTeammates={selectedTeammates}
                        teammates={workspaceUsers}
                        viewDensity={calendarViewDensity}
                        getWorklogColors={getWorklogColors}
                        isAdmin={isAdmin}
                        currentUser={user}
                        user={user}
                        availableProjects={projects}
                        availableTasks={tasks}
                        apiToken={apiToken}
                        hoveredProjectId={hoveredProjectId}
                        showWeekends={showWeekends}
                        setIsLoading={setIsLoading}
                        setLoadingMessage={setLoadingMessage}
                    />
                ) : (
                    <DayView
                        currentDate={currentDate}
                        selectedDate={selectedDate}
                        worklogs={worklogs}
                        calendarEvents={isGoogleCalendarVisible ? calendarEvents : []}
                        isGoogleCalendarVisible={isGoogleCalendarVisible}
                        onWorklogCreate={handleWorklogCreate}
                        onWorklogUpdate={handleWorklogUpdate}
                        onWorklogDelete={handleWorklogDelete}
                        onSuggestionAccept={handleSuggestionAccept}
                        onSuggestionDelete={handleSuggestionDelete}
                        selectedTeammates={selectedTeammates}
                        teammates={workspaceUsers}
                        viewDensity={calendarViewDensity}
                        getWorklogColors={getWorklogColors}
                        isAdmin={isAdmin}
                        currentUser={user}
                        user={user}
                        availableProjects={projects}
                        availableTasks={tasks}
                        apiToken={apiToken}
                        setIsLoading={setIsLoading}
                        setLoadingMessage={setLoadingMessage}
                        recordAction={recordAction}
                    />
                )}
            </div>

            {/* Undo notification */}
            {canUndo && undoState.present && (
                <div className={styles.undoNotification}>
                    <span className={styles.undoText}>
                        {undoState.present.type === 'CREATE' && 'Worklog created'}
                        {undoState.present.type === 'CREATE_SPLIT' && 'Worklogs created (split)'}
                        {undoState.present.type === 'UPDATE' && 'Worklog updated'}
                        {undoState.present.type === 'UPDATE_SPLIT' && 'Worklog updated (split)'}
                        {undoState.present.type === 'DELETE' && 'Worklog deleted'}
                        {undoState.present.type === 'SPLIT' && 'Worklog split'}
                    </span>
                    <button
                        className={styles.undoButton}
                        onClick={performUndo}
                        title="Undo (Cmd+Z)"
                    >
                        ↶ Undo
                        <span className={styles.undoShortcut}>⌘Z</span>
                    </button>
                </div>
            )}
        </div>
    )
}
